import React, { useEffect, useState } from "react";
import { Input } from "@/components/shadcn/ui/input";
import { Textarea } from "@/components/shadcn/ui/textarea";
import { Button } from "@/components/shadcn/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/shadcn/ui/card";
import { useForm } from "react-hook-form";
import { Upload } from "lucide-react";
import axios from "axios"; // Hook para obter o usuário autenticado
import { auth } from "../../../../firebase";
import { useParams } from "react-router-dom";
import { useBusiness } from "@/hook/useBusiness";
import { getTokenFromStorage } from "@/utils/getToken";
import { TokenPayload } from "@/components/navbar/tabs/MenuTabs";
import { jwtDecode } from "jwt-decode";
import { useEmail } from "@/hook/useEmail";

interface EmailForm {
  email: string;
  subject: string;
  message: string;
  file?: File | null;
}

interface Template {
  id: number;
  title: string;
  content: string;
}

interface EmailConfig {
  email: string;
  password: string;
}

const EmailPageLev: React.FC = () => {
  const { register, handleSubmit, setValue, watch, reset } = useForm<EmailForm>();
  const [loading, setLoading] = useState(false);
  const file = watch("file");
  const [templates, setTemplates] = useState<Template[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<number>();
  const [message, setMessage] = useState<string>("");
  const [subject, setSubject] = useState<string>("");
  const [userId, setUserId] = useState<number | null>(null);
  const { leadId } = useParams<{ leadId: string }>();
  const leadId2Set = Number(leadId);
  const { business2, prepareBusiness } = useBusiness(leadId2Set);
  const {getEmailConfig,getTemplates,sendEmail} = useEmail();
  const [emailConfig, setEmailConfig] = useState<EmailConfig | null>(null);

  // 🔹 Obtém a configuração de e-mail do usuário
  useEffect(() => {
    const fetchTokenData = async () => {
      let token = getTokenFromStorage();
      let decodedToken: TokenPayload | undefined;

      if (token) {
        try {
          decodedToken = jwtDecode<TokenPayload>(token);
          if (decodedToken) {
            setUserId(decodedToken.sub)
          }
        } catch (error) {
          console.error('Erro ao decodificar token:', error);
        }
      }

      if (!decodedToken) {
        setTimeout(() => {
          token = getTokenFromStorage();
          if (token) {
            try {
              decodedToken = jwtDecode<TokenPayload>(token);
              if (decodedToken) {
                setUserId(decodedToken.sub)
              }
            } catch (error) {
              console.error('Erro ao decodificar token:', error);
            }
          }
        }, 3000);
      }
    };
    const templates = async () => {
      const response = await getTemplates();
      setTemplates(response);
    }
    templates();
    fetchTokenData();
  }, []);

  useEffect(()=> {
    const EmailConfig = async () => {
      if(userId) {
        const emailConfig = await getEmailConfig(userId);
      setEmailConfig(emailConfig)
      }
    }
    EmailConfig();
  },[userId])

  const handleTemplateChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const templateId = Number(event.target.value);
    setSelectedTemplate(templateId);
    const template = templates.find((t) => t.id === templateId);
    if (template){ 
      setMessage(template.content);
      setSubject(template.title)
    }
};

  const onSubmit = async (data: EmailForm) => {
    if (!emailConfig) {
      alert("Configuração de e-mail não encontrada.");
      return;
    }

    setLoading(true);

    try {
      const formData = new FormData();
      formData.append("email", data.email);
      formData.append("subject", data.subject);
      formData.append("message", data.message);
      formData.append("emailUser", emailConfig.email);
      formData.append("emailPass", emailConfig.password);
      if (data.file) formData.append("file", data.file);

      const reponse = await sendEmail(formData);

      const response2 = await prepareBusiness(leadId2Set);
      alert("E-mail enviado com sucesso!");
      reset();
    } catch (error) {
      console.error(error);
      alert("Erro ao enviar e-mail");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-indigo-200 p-1">
      <Card className="w-full max-w-2xl shadow-lg rounded-lg bg-white">
        <CardHeader>
          <CardTitle className="text-center text-2xl font-bold">Enviar E-mail</CardTitle>
        </CardHeader>
        <CardContent>
          <form className="max-w-sm mx-auto">
            <label htmlFor="templates" className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
              Escolha um template:
            </label>
            <select
              value={selectedTemplate || ""}
              onChange={handleTemplateChange}
              id="templates"
              className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
            >
              <option value="">Selecione um template</option>
              {templates.map((template) => (
                <option key={template.id} value={template.id}>
                  {template.title}
                </option>
              ))}
            </select>
          </form>

          <form onSubmit={handleSubmit(onSubmit)} className="pt-4 space-y-4">
            <Input type="email" placeholder="E-mail do remetente" value={emailConfig?.email} disabled />
            <Input {...register("email", { required: true })} value={business2?.email} type="email" placeholder="E-mail do destinatário" />
            <Input {...register("subject", { required: true })} value={subject} onChange={(e) => setSubject(e.target.value)} type="text" placeholder="Assunto" />
            <Textarea {...register("message", { required: true })} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Mensagem" />

            <div className="flex items-center gap-2">
              <Input
                type="file"
                className="hidden"
                id="file-upload"
                onChange={(e) => setValue("file", e.target.files?.[0] || null)}
              />
              <label htmlFor="file-upload" className="flex items-center gap-2 cursor-pointer">
                <Upload size={20} />
                {file ? file.name : "Anexar arquivo"}
              </label>
            </div>

            <Button type="submit" disabled={loading} className="w-full">
              {loading ? "Enviando..." : "Enviar"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default EmailPageLev;
